import { useCallback, useEffect, useState } from 'react';
import { costing, employees as employeeApi } from '../api/client';
import { isoDate, addDays } from '../util/dates';

const inr = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 });
const money = (v) => (v === null || v === undefined ? '—' : inr.format(Number(v)));
const hrs = (v) => (v ? `${Number(v).toLocaleString('en-IN')}h` : '—');

function presets() {
  const t = new Date();
  const y = t.getFullYear(), m = t.getMonth();
  const d = (yy, mm, dd) => isoDate(new Date(yy, mm, dd));
  const q = Math.floor(m / 3) * 3;
  return [
    { key: 'month', label: 'This month', from: d(y, m, 1), to: isoDate() },
    { key: 'last', label: 'Last month', from: d(y, m - 1, 1), to: d(y, m, 0) },
    { key: 'quarter', label: 'This quarter', from: d(y, q, 1), to: isoDate() },
    // Indian financial year starts 1 April
    { key: 'fy', label: 'Financial year', from: d(m >= 3 ? y : y - 1, 3, 1), to: isoDate() },
  ];
}

/**
 * showMoney is decided by the server (HR/Admin only). The screen only renders
 * what it is given - hiding columns in the browser would not protect pay data
 * that had already been sent.
 */
export default function Costing({ me, onError, onNotice }) {
  const isAdmin = !!me.isAdmin;
  const [view, setView] = useState('projects');
  const P = presets();
  const [range, setRange] = useState(P[0]);

  const views = isAdmin
    ? [{ key: 'projects', label: 'Projects' }, { key: 'util', label: 'Utilisation' }, { key: 'rates', label: 'Salary rates' }]
    : [{ key: 'projects', label: 'Projects' }];

  return (
    <div>
      <div className="row-between">
        <div>
          <h2>{isAdmin ? 'Costing' : 'Project hours'}</h2>
          <p className="muted">
            {isAdmin
              ? 'What each project costs in people’s time: approved hours × each person’s hourly cost.'
              : 'Approved hours your team has booked to each project.'}
          </p>
        </div>
      </div>

      <div className="subtabs">
        {views.map((v) => (
          <button key={v.key} className={`tab ${view === v.key ? 'tab-on' : ''}`}
                  onClick={() => setView(v.key)}>{v.label}</button>
        ))}
        {view !== 'rates' && (
          <div className="range">
            {P.map((p) => (
              <button key={p.key} className={`btn btn-sm ${range.key === p.key ? 'btn-primary' : ''}`}
                      onClick={() => setRange(p)}>{p.label}</button>
            ))}
            <span className="muted small">{range.from} → {range.to}</span>
          </div>
        )}
      </div>

      {view === 'projects' && <Projects range={range} onError={onError} />}
      {view === 'util' && isAdmin && <Utilisation range={range} onError={onError} />}
      {view === 'rates' && isAdmin && <Rates onError={onError} onNotice={onNotice} />}
    </div>
  );
}

function Projects({ range, onError }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    let live = true;
    setData(null); setOpen(null); setDetail(null);
    costing.projects(range.from, range.to)
      .then((d) => live && setData(d))
      .catch((e) => onError(e.message));
    return () => { live = false; };
  }, [range, onError]);

  useEffect(() => {
    if (!open) { setDetail(null); return; }
    costing.project(open, range.from, range.to).then(setDetail).catch((e) => onError(e.message));
  }, [open, range, onError]);

  if (!data) return <p className="muted">Loading…</p>;
  const m = data.showsMoney;
  const t = data.totals;
  const maxCost = Math.max(1, ...data.projects.map((p) => Number(m ? p.approvedCost : p.approvedHours)));

  return (
    <div>
      <div className="strip">
        {m && <Cell v={money(t.approvedCost)} l="Approved cost" />}
        <Cell v={hrs(t.approvedHours)} l="Approved hours" />
        <Cell v={t.approvedHours ? `${Math.round((t.billableHours / t.approvedHours) * 100)}%` : '—'} l="Billable" />
        {m ? <Cell v={money(t.pendingCost)} l="Awaiting approval" />
           : <Cell v={hrs(t.pendingHours)} l="Awaiting approval" />}
        {m && t.uncostedHours > 0 && (
          <div className="strip-warn">
            {t.uncostedHours}h approved but not costed — those people have no salary rate for the dates worked.
            Add it under <strong>Salary rates</strong>; until then these projects look cheaper than they are.
          </div>
        )}
      </div>

      {data.projects.length === 0 ? (
        <div className="card empty">
          <p><strong>No approved or submitted hours in this period.</strong></p>
          <p className="muted">Costs appear as timesheets are submitted and approved.</p>
        </div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Project</th><th className="num">People</th><th className="num">Approved</th>
                <th className="num">Billable</th><th className="num">Pending</th>
                {m && <th className="num">Cost</th>}
                <th style={{ minWidth: 110 }}>{m ? 'Share of cost' : 'Share of hours'}</th>
              </tr>
            </thead>
            <tbody>
              {data.projects.map((p) => (
                <tr key={p.projectId} className={`clickable ${open === p.projectId ? 'row-open' : ''}`}
                    onClick={() => setOpen(open === p.projectId ? null : p.projectId)}>
                  <td>
                    <span className="mono small">{p.code}</span> <strong>{p.name}</strong>
                    {p.client && <div className="small muted">{p.client}</div>}
                  </td>
                  <td className="num">{p.people}</td>
                  <td className="num">{hrs(p.approvedHours)}</td>
                  <td className="num small">{hrs(p.billableHours)}</td>
                  <td className="num small muted">{hrs(p.pendingHours)}</td>
                  {m && (
                    <td className="num">
                      <strong>{p.approvedHours ? money(p.approvedCost) : '—'}</strong>
                      {p.uncostedHours > 0 && <div className="small warn-text">+{p.uncostedHours}h uncosted</div>}
                    </td>
                  )}
                  <td>
                    <div className="bar"><div className="bar-fill"
                      style={{ width: `${(Number(m ? p.approvedCost : p.approvedHours) / maxCost) * 100}%` }} /></div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detail && (
        <div className="card" style={{ marginTop: 16 }}>
          <h3>{detail.code} · {detail.name}</h3>
          <p className="muted small">Who worked on it, {range.from} to {range.to}</p>
          <table>
            <thead>
              <tr><th>Person</th><th className="num">Approved</th><th className="num">Pending</th>{m && <th className="num">Cost</th>}</tr>
            </thead>
            <tbody>
              {detail.people.map((x) => (
                <tr key={x.employeeId}>
                  <td>{x.name}<div className="small muted">{x.employeeCode}{x.designation ? ` · ${x.designation}` : ''}</div></td>
                  <td className="num">{hrs(x.approvedHours)}</td>
                  <td className="num small muted">{hrs(x.pendingHours)}</td>
                  {m && <td className="num">{x.approvedHours ? money(x.approvedCost) : '—'}{x.uncostedHours > 0 && <div className="small warn-text">{x.uncostedHours}h uncosted</div>}</td>}
                </tr>
              ))}
            </tbody>
          </table>
          {detail.months.length > 1 && (
            <p className="small muted" style={{ marginTop: 10 }}>
              By month: {detail.months.map((x) => `${x.month} ${m ? money(x.approvedCost) : hrs(x.approvedHours)}`).join(' · ')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function Utilisation({ range, onError }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let live = true;
    setData(null);
    costing.utilisation(range.from, range.to).then((d) => live && setData(d)).catch((e) => onError(e.message));
    return () => { live = false; };
  }, [range, onError]);

  if (!data) return <p className="muted">Loading…</p>;
  const t = data.totals;
  return (
    <div>
      <div className="strip">
        <Cell v={money(t.capacityCost)} l="Salary cost (working days)" />
        <Cell v={money(t.bookedCost)} l="Booked to projects" />
        <Cell v={money(t.paidLeaveCost)} l="Paid leave" />
        <Cell v={money(t.unbookedCost)} l="Not booked anywhere" />
        <Cell v={`${t.utilisationPct}%`} l="Utilisation" />
      </div>
      <p className="small muted" style={{ marginTop: -6, marginBottom: 12 }}>
        Capacity is working days × {data.hoursPerDay}h, excluding weekends, holidays, unpaid leave and days outside
        employment. “Not booked” is paid time on no project — bench, unlogged internal work, or timesheets not yet approved.
      </p>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Person</th><th className="num">Capacity</th><th className="num">Booked</th>
              <th className="num">Leave</th><th className="num">Unbooked</th><th>Utilisation</th>
              <th className="num">Unbooked cost</th>
            </tr>
          </thead>
          <tbody>
            {data.people.map((p) => (
              <tr key={p.employeeId}>
                <td>{p.name}<div className="small muted mono">{p.employeeCode}</div></td>
                <td className="num">{hrs(p.capacityHours)}</td>
                <td className="num">{hrs(p.bookedHours)}{p.overtimeHours > 0 && <div className="small muted">+{p.overtimeHours}h over</div>}</td>
                <td className="num small">{hrs(p.paidLeaveHours)}</td>
                <td className="num small">{hrs(p.unbookedHours)}</td>
                <td style={{ minWidth: 110 }}>
                  <div className="bar"><div className="bar-fill" style={{ width: `${Math.min(100, p.utilisationPct)}%` }} /></div>
                  <span className="small muted">{p.utilisationPct}%</span>
                </td>
                <td className="num">
                  {p.uncosted ? <span className="small warn-text">no salary rate</span> : money(p.unbookedCost)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Rates({ onError, onNotice }) {
  const [rows, setRows] = useState([]);
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState(null);     // {employeeId, name, monthlyCtc, effectiveFrom, note}
  const [history, setHistory] = useState({}); // employeeId -> rates
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([costing.rates(), employeeApi.list()]);
      setRows(r || []); setStaff(s || []);
    } catch (e) { onError(e.message); } finally { setLoading(false); }
  }, [onError]);
  useEffect(() => { load(); }, [load]);

  async function save(e) {
    e.preventDefault();
    try {
      await costing.addRate({ ...form, monthlyCtc: Number(form.monthlyCtc) });
      onNotice(`Rate saved for ${form.name} from ${form.effectiveFrom}`);
      setForm(null); setHistory({});
      await load();
    } catch (err) { onError(err.message); }
  }

  async function toggleHistory(id) {
    if (history[id]) { const h = { ...history }; delete h[id]; setHistory(h); return; }
    try { setHistory({ ...history, [id]: await costing.history(id) }); } catch (e) { onError(e.message); }
  }

  async function remove(rate, row) {
    if (!window.confirm(`Delete the rate from ${rate.effectiveFrom} for ${row.name}? Costs for those dates will be recalculated.`)) return;
    try {
      await costing.deleteRate(rate.id);
      onNotice('Rate deleted');
      setHistory({}); await load();
    } catch (e) { onError(e.message); }
  }

  if (loading) return <p className="muted">Loading…</p>;
  const missing = rows.filter((r) => !r.monthlyCtc).length;

  return (
    <div>
      <div className="alert alert-info">
        Salary data — visible to HR/Admin only. Hourly cost = monthly CTC ÷ 176h. Add a new rate for a raise
        rather than editing: past project costs keep the rate that applied at the time.
        {missing > 0 && <strong> {missing} people have no rate yet.</strong>}
      </div>

      {form && (
        <form className="card form-grid" onSubmit={save}>
          <h3 className="span-all">Set monthly CTC · {form.name}</h3>
          <label className="field"><span>Monthly CTC (₹)</span>
            <input type="number" min="1" step="1" value={form.monthlyCtc} required autoFocus
                   onChange={(e) => setForm({ ...form, monthlyCtc: e.target.value })} />
            {form.monthlyCtc > 0 && <span className="small muted">≈ {money(form.monthlyCtc / 176)} per hour</span>}
          </label>
          <label className="field"><span>Effective from</span>
            <input type="date" value={form.effectiveFrom} required
                   onChange={(e) => setForm({ ...form, effectiveFrom: e.target.value })} />
          </label>
          <label className="field span-all"><span>Note</span>
            <input value={form.note} placeholder="e.g. Annual increment" onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </label>
          <div className="span-all actions">
            <button className="btn btn-primary">Save rate</button>
            <button type="button" className="btn" onClick={() => setForm(null)}>Cancel</button>
          </div>
        </form>
      )}

      <div className="table-wrap">
        <table>
          <thead>
            <tr><th>Person</th><th className="num">Monthly CTC</th><th className="num">Hourly cost</th><th>Since</th><th></th></tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const emp = staff.find((s) => s.id === r.employeeId);
              return [
                <tr key={r.employeeId}>
                  <td>{r.name}<div className="small muted">{r.employeeCode}{r.designation ? ` · ${r.designation}` : ''}</div></td>
                  <td className="num">{r.monthlyCtc ? money(r.monthlyCtc) : <span className="warn-text small">not set</span>}
                    {r.nextCtc && <div className="small muted">{money(r.nextCtc)} from {r.nextFrom}</div>}</td>
                  <td className="num">{r.hourlyCost ? money(r.hourlyCost) : '—'}</td>
                  <td className="small">{r.effectiveFrom || '—'}</td>
                  <td className="nowrap">
                    <button className="btn btn-sm btn-primary" onClick={() => setForm({
                      employeeId: r.employeeId, name: r.name, monthlyCtc: '', note: '',
                      effectiveFrom: r.monthlyCtc ? isoDate() : (emp && emp.dateOfJoining) || addDays(isoDate(), 0),
                    })}>{r.monthlyCtc ? 'New rate' : 'Set rate'}</button>
                    {r.rateCount > 0 && (
                      <button className="btn btn-sm" onClick={() => toggleHistory(r.employeeId)}>
                        {history[r.employeeId] ? 'Hide' : `History (${r.rateCount})`}
                      </button>
                    )}
                  </td>
                </tr>,
                history[r.employeeId] && (
                  <tr key={`${r.employeeId}-h`} className="history-row">
                    <td colSpan={5}>
                      {history[r.employeeId].map((h) => (
                        <div key={h.id} className="history-item">
                          <span className="mono small">{h.effectiveFrom}</span>
                          <span>{money(h.monthlyCtc)}/month · {money(h.monthlyCtc / h.hoursPerMonth)}/h</span>
                          {h.note && <span className="muted small">{h.note}</span>}
                          <button className="linklike small" onClick={() => remove(h, r)}>Delete</button>
                        </div>
                      ))}
                    </td>
                  </tr>
                ),
              ];
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Cell({ v, l }) {
  return <div className="strip-cell"><div className="strip-value">{v}</div><div className="strip-label">{l}</div></div>;
}
